import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Link, Typography, Box, Divider,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import { getTenant } from "../../config/tenants";
import { useI18n } from "../../i18n";

// ---------------------------------------------------------------------------
// הצהרת נגישות (Accessibility Statement) — required by the Israeli regulations
// (תקנות שוויון זכויות לאנשים עם מוגבלות — התאמות נגישות לשירות, תשע"ג-2013;
// ת"י 5568 / WCAG 2.0 AA). Rendered site-wide (every screen, incl. login) via a
// small trigger link + an accessible MUI Dialog (focus-trap + Esc + ARIA built-in).
//
// Per-tenant data: the accessibility COORDINATOR (רכז נגישות) and the body name
// differ per client (each municipality has its own coordinator by law), so they
// come from the tenant config (config/tenants.js → `a11y`). When not configured
// yet, a clear fallback notice is shown instead of fake data.
//
// NOTE: the conformance wording is intentionally conservative ("בתהליך התאמה")
// — do NOT upgrade it to a full-conformance declaration until a certified
// accessibility auditor (מורשה נגישות שירות / מנ"ש) signs off.
// ---------------------------------------------------------------------------

export default function AccessibilityStatement({ className, showAlways } = {}) {
  const [open, setOpen] = useState(false);
  const { t, dir } = useI18n();
  const isAuthenticated = useSelector((s) => s.auth && s.auth.isAuthenticated);
  // Mobile: show the link only on the initial (login) screen (hidden once logged in).
  // Desktop (showAlways): keep it visible on every screen — it sits in the page's
  // bottom-left corner OUTSIDE the phone frame, where it never interferes.
  if (isAuthenticated && !showAlways) return null;
  const a11y = getTenant().a11y || {};
  /* ⚠️ ‏`bodyName` הוא **שם הרשות** מקונפיגורציית-הלקוח ולכן נשאר בעברית גם
     בממשק מתורגם — שם רשמי של גוף ציבורי אינו מתורגם. רק ברירת-המחדל
     הגנרית ("הרשות") מתורגמת. */
  const bodyName = a11y.bodyName || t("a11y.defaultBodyName");
  const hasCoordinator = a11y.coordinatorName || a11y.coordinatorPhone || a11y.coordinatorEmail;
  // optional list of known accessibility exemptions (פטורים) per tenant — only shown if set
  const exemptions = Array.isArray(a11y.exemptions) ? a11y.exemptions : [];
  const H3 = { fontWeight: 700, color: "#10204d", mt: 2, mb: 0.5 };
  /* ★★ ‏`pr` (padding-right) היה נכון ל-RTL בלבד: הוא הזיח את הרשימה מהצד
     שממנו היא מתחילה. ‏`paddingInlineStart` עושה זאת בשתי הכיווניות.
     ⚠️ ‏MUI מריץ את ה-`sx` דרך emotion, ולכן `pr` **מתהפך** ע"י
        `stylis-plugin-rtl` — אבל רק כשה-cache הוא ה-RTL. תכונה לוגית
        אינה תלויה בכך כלל, וזה בדיוק מה שמבוקש. */
  const UL = { paddingInlineStart: 3, m: 0, "& li": { mb: 0.5, lineHeight: 1.6, color: "#2b3a52" } };

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        underline="hover"
        className={className}
        sx={{
          display: "inline-flex", alignItems: "center", gap: "5px",
          color: "#5b6b7f", fontSize: 13, fontWeight: 600, margin: "14px auto 8px",
          background: "none", border: "none", cursor: "pointer",
        }}
      >
        <AccessibilityNewRoundedIcon sx={{ fontSize: 17 }} aria-hidden="true" />
        {t("a11y.link")}
      </Link>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        /* ★ היה `dir="rtl"` קשיח. הדיאלוג מרונדר ב-portal מחוץ ל-
           `.phone_container`, ולכן הוא **אינו** יורש ממנו את הכיווניות
           וחייב לקבל אותה במפורש. */
        dir={dir}
        aria-labelledby="a11y-statement-title"
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="a11y-statement-title" sx={{ pr: 6, fontWeight: 800, color: "#10204d" }}>
          {t("a11y.dialogTitle")}
          <IconButton
            onClick={() => setOpen(false)}
            aria-label={t("a11y.closeAria")}
            /* ★★ ‏`insetInlineEnd` במקום `right`: הכפתור צריך לשבת בקצה
               **הנגדי** לכותרת בשתי הכיווניות. ההסתמכות הקודמת על היפוך-
               הכיווניות של stylis עבדה רק כל עוד ה-cache היה תמיד RTL —
               מרגע ש-LTR אפשרי, ההיפוך אינו קורה והכפתור היה דורס את
               הכותרת ברוסית/אנגלית. */
            sx={{ position: "absolute", insetInlineEnd: 8, top: 8, color: "#5b6b7f" }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        {/* tabIndex=0 → the scrollable content is keyboard-focusable (WCAG 2.1.1),
            so keyboard users can scroll the long statement with arrow keys. */}
        <DialogContent dividers tabIndex={0} sx={{
            "& p": { mb: 1.5, lineHeight: 1.7, color: "#2b3a52" },
            // Always-visible scrollbar so users realise the statement scrolls (mobile + desktop).
            // overflowY:scroll keeps the track present even before scrolling; the rest styles it.
            overflowY: "scroll",
            scrollbarWidth: "thin",
            scrollbarColor: "#9fb0c4 #eef1f5",
            "&::-webkit-scrollbar": { width: "10px", WebkitAppearance: "none" },
            "&::-webkit-scrollbar-track": { background: "#eef1f5", borderRadius: "8px" },
            "&::-webkit-scrollbar-thumb": { background: "#9fb0c4", borderRadius: "8px", border: "2px solid #eef1f5" },
            "&::-webkit-scrollbar-thumb:hover": { background: "#7d8ea6" },
          }}>
          <Typography component="p">{t("a11y.intro", { body: bodyName })}</Typography>

          <Typography component="h3" sx={H3}>{t("a11y.levelTitle")}</Typography>
          <Typography component="p">{t("a11y.levelBody")}</Typography>

          <Typography component="h3" sx={H3}>{t("a11y.keyboardTitle")}</Typography>
          <Typography component="p">{t("a11y.keyboardBody")}</Typography>

          <Typography component="h3" sx={H3}>{t("a11y.visionTitle")}</Typography>
          <Typography component="p">{t("a11y.visionBody")}</Typography>

          <Typography component="h3" sx={H3}>{t("a11y.screenReaderTitle")}</Typography>
          <Box component="ul" sx={UL}>
            <li>{t("a11y.sr1")}</li>
            <li>{t("a11y.sr2")}</li>
            <li>{t("a11y.sr3")}</li>
            <li>{t("a11y.sr4")}</li>
            <li>{t("a11y.sr5")}</li>
          </Box>

          {exemptions.length > 0 && (
            <>
              <Typography component="h3" sx={H3}>{t("a11y.exemptionsTitle")}</Typography>
              <Box component="ul" sx={UL}>
                {exemptions.map((ex, i) => <li key={i}>{ex}</li>)}
              </Box>
            </>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Typography component="h3" sx={{ ...H3, mt: 0 }}>{t("a11y.coordinatorTitle")}</Typography>
          <Typography component="p">{t("a11y.coordinatorBody")}</Typography>
          {hasCoordinator ? (
            <Box component="dl" sx={{ m: 0, "& div": { mb: 0.5 } }}>
              {a11y.coordinatorName && (
                <div><strong>{t("a11y.coordinatorName")}</strong>{a11y.coordinatorName}</div>
              )}
              {a11y.coordinatorPhone && (
                <div>
                  <strong>{t("a11y.coordinatorPhone")}</strong>
                  {/* ⚠️ ‏`direction:"ltr"` על מספר-טלפון מתהפך ל-rtl תחת
                      `stylis-plugin-rtl` (מלכודת מתועדת). ‏`unicode-bidi`
                      עם `isolate` משיג את אותה מטרה — בידוד המספר מהמשפט —
                      בלי להצהיר כיווניות שהתוסף יהפוך. */}
                  <Link href={`tel:${a11y.coordinatorPhone}`} sx={{ display: "inline-block", unicodeBidi: "isolate" }}>
                    {a11y.coordinatorPhone}
                  </Link>
                </div>
              )}
              {a11y.coordinatorEmail && (
                <div>
                  <strong>{t("a11y.coordinatorEmail")}</strong>
                  <Link href={`mailto:${a11y.coordinatorEmail}`}>{a11y.coordinatorEmail}</Link>
                </div>
              )}
            </Box>
          ) : (
            <Typography component="p" sx={{ color: "#5b6b7f" }}>
              {t("a11y.noCoordinator", { body: bodyName })}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
